import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CompanyDetailsDto, CompanyDetailsSummaryDto } from '../../models/api/companyDetailsDto';
import { CreateCompanyRequest } from '../../models/api/createCompanyRequest';
import { UpsertCompanyRequest } from '../../models/api/upsertCompanyRequest';
import { YearlyResource } from '../../models/api/yearlyResource';

@Injectable({
  providedIn: 'root'
})
export class CompanyDetailsService {
  private apiUrl = `${environment.apiUrl}/companies`;

  constructor(private http: HttpClient) {}

  /**
   * Create company details for an existing company
   */
  create(companyId: string, request: CreateCompanyRequest): Observable<CompanyDetailsDto> {
    return this.http.post<CompanyDetailsDto>(`${this.apiUrl}/${companyId}/details`, request);
  }

  /**
   * Get company details by company ID for a specific year. Uses the YearlyResource envelope —
   * state may be 'present' (record explicitly entered for the year), 'inherited' (older record
   * still in force; hints.effectiveYear identifies the source year), or 'absent'.
   */
  get(companyId: string, year?: number): Observable<YearlyResource<CompanyDetailsDto>> {
    let params = new HttpParams();
    if (year !== undefined) {
      params = params.set('year', year.toString());
    }
    return this.http.get<YearlyResource<CompanyDetailsDto>>(`${this.apiUrl}/${companyId}/details`, { params });
  }

  /**
   * Get company ID and name for a specific year (lightweight)
   */
  getSummary(companyId: string, year: number): Observable<CompanyDetailsSummaryDto> {
    const params = new HttpParams().set('year', year.toString());
    return this.http.get<CompanyDetailsSummaryDto>(`${this.apiUrl}/${companyId}/details/summary`, { params });
  }

  /**
   * Upsert company details for a specific year
   */
  upsert(companyId: string, request: UpsertCompanyRequest): Observable<CompanyDetailsDto> {
    return this.http.put<CompanyDetailsDto>(`${this.apiUrl}/${companyId}/details`, request);
  }
}
